import React, { useEffect, useState } from "react";
import AgentsCrumb from "./AgentsCrumb";
import { observer } from "mobx-react";
import { useHistory, useLocation } from "react-router-dom";
import { agentsData } from "Stores/agentsData";

const timeAgo = ts => {
  if (!ts) return "";
  const d = typeof ts === "number" ? ts : new Date(ts).getTime();
  const mins = Math.max(0, Math.round((Date.now() - d) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

/** One pending batch: pick which contacts to enroll, then approve or reject. */
const ApprovalCard = observer(({ approval, onDecided }) => {
  const items = approval.items || [];
  const [selected, setSelected] = useState(() => new Set(items.map(i => i.contactId)));
  const [confirmReject, setConfirmReject] = useState(false);

  const toggle = id => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const allChecked = items.length > 0 && selected.size === items.length;
  const toggleAll = () =>
    setSelected(allChecked ? new Set() : new Set(items.map(i => i.contactId)));

  const approve = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const subset = ids.length === items.length ? null : ids;
    if (await agentsData.approveBatch(approval.id, subset)) onDecided(approval, "approved", ids.length);
  };
  const reject = async () => {
    if (await agentsData.rejectBatch(approval.id)) onDecided(approval, "rejected", items.length);
  };

  return (
    <div className="ag-appr-card">
      <div className="ag-appr-card__head">
        <div>
          <div className="ag-appr-card__agent">{approval.agentName || "Agent"}</div>
          <div className="ag-appr-card__meta">
            {items.length} contact{items.length === 1 ? "" : "s"} awaiting review ·{" "}
            {timeAgo(approval.createdAt)}
          </div>
        </div>
        <div className="ag-appr-card__count">{selected.size}/{items.length}</div>
      </div>

      <div className="ag-appr-table">
        <div className="ag-appr-row ag-appr-row--head">
          <label className="ag-appr-check">
            <input type="checkbox" checked={allChecked} onChange={toggleAll} />
          </label>
          <div className="ag-appr-c ag-appr-c--name">Name</div>
          <div className="ag-appr-c ag-appr-c--email">Email</div>
          <div className="ag-appr-c ag-appr-c--co">Company</div>
        </div>
        {items.map(it => {
          const data = it.data || {};
          const company = data.companyName || data.company || data.accountName || "";
          return (
            <div key={it.contactId} className="ag-appr-row">
              <label className="ag-appr-check">
                <input
                  type="checkbox"
                  checked={selected.has(it.contactId)}
                  onChange={() => toggle(it.contactId)}
                />
              </label>
              <div className="ag-appr-c ag-appr-c--name">{it.name || "—"}</div>
              <div className="ag-appr-c ag-appr-c--email">{it.email || <span className="ag-appr-muted">no email</span>}</div>
              <div className="ag-appr-c ag-appr-c--co">{company || "—"}</div>
            </div>
          );
        })}
      </div>

      <div className="ag-appr-card__foot">
        {confirmReject ? (
          <>
            <span className="ag-appr-confirm">Reject all {items.length}? The agent won't act on any of them.</span>
            <button className="ag-btn ag-btn--ghost" onClick={() => setConfirmReject(false)}><i className="material-icons-outlined">close</i>Cancel</button>
            <button className="ag-btn ag-btn--danger" onClick={reject} disabled={agentsData.busy}><i className="material-icons-outlined">block</i>Yes, reject</button>
          </>
        ) : (
          <>
            <button className="ag-btn ag-btn--ghost" onClick={() => setConfirmReject(true)} disabled={agentsData.busy}><i className="material-icons-outlined">block</i>Reject all</button>
            <button className="ag-btn ag-btn--primary" onClick={approve} disabled={agentsData.busy || selected.size === 0}><i className="material-icons-outlined">check_circle</i>
              Approve {selected.size > 0 ? `(${selected.size})` : ""}
            </button>
          </>
        )}
      </div>
    </div>
  );
});

const ApprovalsQueue = observer(() => {
  const history = useHistory();
  // ?agent=<id> (from an agent card's "Review N contacts") narrows the queue to that agent.
  const onlyAgent = new URLSearchParams(useLocation().search).get("agent");
  // Decisions made on this visit, so the page confirms them instead of jumping to "Nothing to review".
  const [decided, setDecided] = useState([]);
  const onDecided = (a, kind, count) =>
    setDecided(d => [{ id: a.id, agentId: a.agentId, runId: a.runId, agentName: a.agentName, kind, count }, ...d]);

  useEffect(() => {
    agentsData.fetchApprovals();
  }, []);

  const { approvalsLoading } = agentsData;
  const approvals = onlyAgent ? agentsData.approvals.filter(a => a.agentId === onlyAgent) : agentsData.approvals;

  return (
    <div className="ag-page">
      <AgentsCrumb items={[{ label: "Approvals" }]} />
      <div className="ag-page__head">
        <div>
          <h1 className="ag-page__title">Approvals</h1>
          <p className="ag-page__sub">
            Review the contacts your agents found before they act on them.
          </p>
        </div>
        <div className="ag-actions">
          {onlyAgent && agentsData.approvals.length > approvals.length && (
            <button className="ag-btn ag-btn--sm" onClick={() => history.push("/agents/approvals")}><i className="material-icons-outlined">clear_all</i>
              Show all {agentsData.approvals.length}
            </button>
          )}
        </div>
      </div>

      {approvalsLoading && approvals.length === 0 && <div className="ag-skel ag-skel--rows"><span className="linear-background" /><span className="linear-background" /><span className="linear-background" /></div>}

      {decided.length > 0 && (
        <div className="ag-appr-list">
          {decided.map(d => (
            <div key={d.id} className="ag-appr-card ag-appr-card--done">
              <i className={`material-icons-outlined ag-appr-result ag-appr-result--${d.kind}`}>{d.kind === "approved" ? "check_circle" : "block"}</i>
              <div className="ag-appr-card__body">
                <div className={`ag-appr-result ag-appr-result--${d.kind}`}>
                  {d.kind === "approved"
                    ? `Approved ${d.count} contact${d.count === 1 ? "" : "s"}. The agent is working on them now.`
                    : `Rejected ${d.count} contact${d.count === 1 ? "" : "s"}. Nothing was done with them.`}
                </div>
                <div className="ag-appr-card__meta">{d.agentName || "Agent"}</div>
              </div>
              {d.kind === "approved" && d.agentId && d.runId && (
                <button className="ag-btn ag-btn--sm" onClick={() => history.push(`/agents/${d.agentId}/runs/${d.runId}`)}>
                  Watch the run<i className="material-icons-outlined">arrow_forward</i>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!approvalsLoading && approvals.length === 0 && (
        <div className={`ag-empty${decided.length > 0 ? " ag-empty--quiet" : ""}`}>
          <div className="ag-empty__title">{decided.length > 0 ? "All caught up" : "Nothing to review"}</div>
          <div className="ag-empty__text">
            {decided.length > 0
              ? "Every batch is decided. New ones show up here when your agents find more contacts."
              : "When an agent that needs your approval finds contacts, they’ll show up here."}
          </div>
        </div>
      )}

      {approvals.length > 0 && (
        <div className="ag-appr-list">
          {approvals.map(a => (
            <ApprovalCard key={a.id} approval={a} onDecided={onDecided} />
          ))}
        </div>
      )}
    </div>
  );
});

export default ApprovalsQueue;
